import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Box, Typography } from '@mui/material';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import { VideoModelSection } from '../components/models';
import { useVideoModels } from '../hooks/useVideoModels';
import Loading from '../components/common/Loading';
import { ModelCard as ModelCardType } from '../components/models/types';

const ModalVideoPage: React.FC = () => {
  const navigate = useNavigate();
  const { sections, isLoading } = useVideoModels();
  const location = useLocation();

  const handleModelClick = (model: ModelCardType) => {
    // Navigate back to FairGen Studio with selected model
    const returnPath = (location.state as any)?.returnPath || '/fairgen-ai-studio';
    const contentTypeTab = (location.state as any)?.contentTypeTab || 'video';
    navigate(returnPath, {
      state: {
        selectedVideoModel: model,
        contentTypeTab: contentTypeTab
      },
      replace: false
    });
  };

  const handleBackClick = () => {
    // Use returnPath if available, otherwise go back in history
    const returnPath = (location.state as any)?.returnPath;
    const contentTypeTab = (location.state as any)?.contentTypeTab;
    if (returnPath) {
      navigate(returnPath, {
        state: contentTypeTab ? { contentTypeTab } : undefined
      });
    } else {
      navigate(-1);
    }
  };

  // Note: Video model selection is handled via navigation state in VideoCanvaForm
  // For now, we don't track selected state here since video models use local state
  // This can be enhanced later to use a shared context/state management
  const sectionsWithSelection = sections;

  if (isLoading) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          backgroundColor: '#000000',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center'
        }}
      >
        <Loading />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        backgroundColor: '#000000',
        color: '#FFFFFF',
        fontFamily: '"Inter", sans-serif',
        overflowX: 'hidden',
        paddingTop: '40px'
      }}
    >
      {/* Back Link */}
      <Box
        onClick={handleBackClick}
        sx={{
          cursor: 'pointer',
          marginBottom: '24px',
          display: 'flex',
          alignItems: 'center',
          color: '#FFFFFF',
          fontSize: '16px',
          fontFamily: '"Inter", sans-serif',
          '&:hover': { opacity: 0.8 }
        }}
      >
        <ArrowBackIosNewIcon sx={{ fontSize: '14px', marginRight: '8px' }} />
        Back
      </Box>

      {/* Page Title */}
      <Box sx={{ marginBottom: '24px' }}>
        <Typography
          variant="h1"
          sx={{
            fontSize: '40px',
            fontWeight: 700,
            fontFamily: '"Inter", sans-serif',
            background: 'linear-gradient(89.99deg, #1CB0C8 0.01%, #FF3DA3 99.99%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            width: 'fit-content'
          }}
        >
          Models
        </Typography>
      </Box>

      {/* Sections */}
      {sectionsWithSelection.map((section) => (
        <VideoModelSection
          key={section.title}
          section={section}
          onModelClick={handleModelClick}
        />
      ))}
    </Box>
  );
};

export default ModalVideoPage;